// react
import React, { useState } from 'react'
import classNames from 'classnames'
import {
  Button,
  BUTTON_THEME,
  Modal,
  CheckBox,
  Icon,
  Input,
} from '@st-operations/uikit/src/v2/index.js'

import { useUser } from '_hooks'

import './SelectSlidesModal.scss'
import {
  AVAILABLE_SLIDES,
  CATEGORIES,
  SLIDES,
  MARKETING_SLIDES,
  DEFAULT_SLIDES,
} from './constants/availableSlides'
import { SLIDES_SCHEMA } from './constants/slidesSchema'
import thumbnails from './template/thumbnails'

import { createReport } from '_services/insights-v2'

const SELECTED_AND_SHOWN = {
  NONE: 'none',
  SOME: 'some',
  ALL: 'all',
}

// TODO: add loading state to improve UX
const SelectSlidesModal = ({
  open,
  onClose,
  audienceId,
  audienceName,
  onSuccess,
  populateDefault,
}) => {
  useUser()
  const [categoriesSelected, setCategoriesSelected] = useState([])
  const [selectedSlides, setSelectedSlides] = useState(
    Object.fromEntries(
      AVAILABLE_SLIDES.map((slide) => [
        slide.template,
        DEFAULT_SLIDES.includes(slide.template) ? 1 : 0,
      ])
    )
  )
  const [isShowOnlySelected, setIsShowOnlySelected] = useState(false)
  const [search, setSearch] = useState('')

  const filteredSlides = AVAILABLE_SLIDES
    // categories
    .filter((slide) => {
      if (categoriesSelected.length !== 0) {
        return categoriesSelected.includes(slide.category)
      }
      return true
    })
    // selected
    .filter((slide) => {
      if (isShowOnlySelected) {
        return selectedSlides[slide.template] !== 0
      }
      return true
    })
    // search
    .filter(({ category, tags, template, title }) => {
      if (!!search) {
        const textTokens = `${category} ${template} ${title} ${tags?.join(' ') ?? ''}`
          .replaceAll('_', ' ')
          .toLowerCase()
        return textTokens.includes(search.toLowerCase())
      }
      return true
    })

  const handleSelectCategory = (category) => () => {
    setCategoriesSelected((prev) => {
      if (prev.includes(category)) {
        return prev.filter((cat) => cat !== category)
      }
      return [...prev, category]
    })
  }

  const handleClearCategories = () => {
    setCategoriesSelected([])
  }

  const handleSelectSlide = ({ target: { name: template, checked } }) => {
    setSelectedSlides((prev) => ({ ...prev, [template]: checked ? 1 : 0 }))
  }

  const handleChangeQuantity = (type, template) => () => {
    setSelectedSlides((prev) => ({
      ...prev,
      [template]: type === 'add' ? prev[template] + 1 : prev[template] - 1,
    }))
  }

  const selectedAndShown = (() => {
    switch (filteredSlides.filter(({ template }) => selectedSlides[template] !== 0).length) {
      case 0:
        return SELECTED_AND_SHOWN.NONE
      case filteredSlides.length:
        return SELECTED_AND_SHOWN.ALL
      default:
        return SELECTED_AND_SHOWN.SOME
    }
  })()

  const handleSelectAll = () => {
    const changedSlides = filteredSlides.reduce(
      (acc, { template }) =>
        selectedAndShown === SELECTED_AND_SHOWN.ALL
          ? { ...acc, [template]: 0 }
          : { ...acc, [template]: selectedSlides[template] || 1 },
      {}
    )
    setSelectedSlides((prev) => ({ ...prev, ...changedSlides }))
  }

  const handleClearAll = () => {
    setSelectedSlides(Object.fromEntries(AVAILABLE_SLIDES.map((slide) => [slide.template, 0])))
  }

  const getFromSchema = (templates) =>
    templates.map((template) =>
      JSON.parse(JSON.stringify(SLIDES_SCHEMA.find((schema) => schema.template === template)))
    )

  const handleCreateDeck = () => {
    const templates = Object.entries(selectedSlides)
      .flatMap(([template, quantity]) => new Array(quantity).fill(template))
      .flatMap((template) => (template === SLIDES.MARKETING ? MARKETING_SLIDES : template))
    const slides = getFromSchema(['Intro', ...templates, 'Outro'])
    const populated = slides.map((slide, index) => populateDefault({ slides }, index))
    createReport({ audienceId, slides: populated, audienceName })
      .then((report) => onSuccess(report))
      .catch((_err) => toast.error('Error selecting slides.'))
  }

  const handleCreateDefault = () => {
    const slides = getFromSchema(DEFAULT_SLIDES)
    createReport({ audienceId, slides, audienceName })
      .then((report) => onSuccess(report))
      .catch((_err) => toast.error('Error selecting slides.'))
  }

  return (
    <Modal onClose={onClose} isOpen={open} className="create-insights-modal" size="large">
      <div className="select-slides">
        <h1 className="select-slides__title">Select Slides</h1>
        <Input
          className="select-slides__search"
          theme="search"
          placeholder="Search Slides"
          value={search}
          onChange={(value) => setSearch(value)}
        />
        <div className="select-slides__sidebar">
          <button
            onClick={handleClearCategories}
            className={classNames({
              'select-slides__category': true,
              'select-slides__category--selected': categoriesSelected.length === 0,
            })}
          >
            All Slides
          </button>
          <For each="category" index="i" of={Object.values(CATEGORIES)}>
            <button
              key={i}
              onClick={handleSelectCategory(category)}
              className={classNames({
                'select-slides__category': true,
                'select-slides__category--selected': categoriesSelected.includes(category),
              })}
            >
              {category}
            </button>
          </For>
        </div>
        <div className="select-slides__actions">
          <CheckBox
            label="Select All"
            className="select-slides__action"
            checked={selectedAndShown === SELECTED_AND_SHOWN.ALL}
            isIndeterminate={selectedAndShown === SELECTED_AND_SHOWN.SOME}
            onChange={handleSelectAll}
          />
          <CheckBox
            label={`Show Only Selected (${
              Object.values(selectedSlides).filter((value) => value > 0).length
            })`}
            className="select-slides__action"
            checked={isShowOnlySelected}
            onChange={() => setIsShowOnlySelected((prev) => !prev)}
          />
          <Button
            className="select-slides__action"
            onClick={handleClearAll}
            theme={BUTTON_THEME.GHOST}
            style={{ marginLeft: 'auto' }}
          >
            <Icon icon="trashcan" type="outline" color="#13682B" width="22px" height="22px" />
            Clear All
          </Button>
        </div>
        <div className="select-slides__content">
          <div className="select-slides__slides">
            <For each="slide" index="index" of={filteredSlides}>
              <div key={index} className="slide">
                <CheckBox
                  name={slide.template}
                  label={slide.title}
                  checked={!!selectedSlides[slide.template]}
                  className="slide__checkbox"
                  onChange={handleSelectSlide}
                />
                <img
                  src={thumbnails[slide.template] ?? thumbnails.defaultImage}
                  alt=""
                  className="slide__image"
                />
                <div className="slide__actions">
                  <Button
                    className="slide__action"
                    disabled={selectedSlides[slide.template] === 0}
                    onClick={handleChangeQuantity('remove', slide.template)}
                    theme={BUTTON_THEME.GHOST}
                  >
                    <Icon icon="minus" type="fill" width={14} height={14} />
                  </Button>
                  <span>{selectedSlides[slide.template]}</span>
                  <Button
                    className="slide__action"
                    onClick={handleChangeQuantity('add', slide.template)}
                    theme={BUTTON_THEME.GHOST}
                  >
                    <Icon icon="plus2" type="fill" width={14} height={14} />
                  </Button>
                </div>
              </div>
            </For>
          </div>
        </div>
        <footer className="select-slides__footer">
          <Button
            className="select-slides__button"
            onClick={onClose}
            theme={BUTTON_THEME.GHOST}
            style={{ marginRight: 'auto' }}
          >
            Cancel
          </Button>
          <Button
            className="select-slides__button"
            onClick={handleCreateDefault}
            theme={BUTTON_THEME.SECONDARY}
            style={{ flexBasis: 'max-content' }}
          >
            Create with Default
          </Button>
          <Button className="select-slides__button" onClick={handleCreateDeck}>
            Create Deck
          </Button>
        </footer>
      </div>
    </Modal>
  )
}

const SelectSlidesModalStateCleanup = ({ open, ...rest }) => {
  if (open) return <SelectSlidesModal open={open} {...rest} />
  return null
}

export default SelectSlidesModalStateCleanup
